import { useMemo } from 'react';

import { Button, Input } from '@parrot-software/design-system';
import { LoaderCircle, ShieldCheck } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';

import { IDENTITY_ERROR_CODES } from '@identity/application/errors';
import { FieldLabel, StepCard } from '@identity/presentation/components/base';
import { createEmailFormSchema } from '@identity/presentation/components/specific/steps';
import type { EmailFormValues } from '@identity/presentation/components/specific/steps';
import { useRequestPasswordRecovery } from '@identity/presentation/hooks';
import { identityErrorKey, translateIdentityError } from '@identity/presentation/utils';

/**
 * The one failure that belongs under the field instead of under the button: an invalid format is a
 * fact about the address, and it is the only thing the backend may say about it.
 */
const EMAIL_INVALID_ERROR_KEY = identityErrorKey(IDENTITY_ERROR_CODES.EMAIL_INVALID);

export type RecoveryEmailViewProps = {
  initialEmail: string;
  onRequested: (email: string) => void;
};

/**
 * Capture view of the recovery request.
 *
 * @param {RecoveryEmailViewProps} props - Component properties
 */
export const RecoveryEmailView = ({ initialEmail, onRequested }: RecoveryEmailViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, request } = useRequestPasswordRecovery();
  const schema = useMemo(() => createEmailFormSchema(), []);
  const resolver = useMemo(() => (yupResolver(schema) as unknown) as Resolver<EmailFormValues>, [schema]);

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<EmailFormValues>({
    defaultValues: { email: initialEmail },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const email = watch('email');
  const isEmailAccepted = schema.isValidSync({ email });
  const validationErrorKey = errors.email?.message;
  // The format is about the address; a rate limit or a lost connection is about the request.
  const fieldErrorKey = validationErrorKey ?? (errorKey === EMAIL_INVALID_ERROR_KEY ? errorKey : undefined);
  const requestErrorKey = errorKey === EMAIL_INVALID_ERROR_KEY ? undefined : errorKey;

  const { onChange: onFieldChange, ...emailField } = register('email');

  const onSubmit = handleSubmit(async (values) => {
    const wasAccepted = await request({ email: values.email });

    if (wasAccepted) onRequested(values.email);
  });

  return (
    <StepCard
      description={t('recovery.request.subtitle')}
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <ShieldCheck aria-hidden className="size-4 text-info" />
        </span>
      }
      title={t('recovery.request.title')}
    >
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-recovery-email" isRequired>
            {t('recovery.request.emailLabel')}
          </FieldLabel>
          <Input
            {...emailField}
            aria-invalid={Boolean(fieldErrorKey)}
            autoComplete="email"
            className={cn({ 'border-destructive focus-visible:ring-destructive': Boolean(fieldErrorKey) })}
            id="identity-recovery-email"
            onChange={(event) => {
              if (validationErrorKey) clearErrors('email');

              return onFieldChange(event);
            }}
            placeholder={t('recovery.request.emailPlaceholder')}
            type="email"
          />
          {fieldErrorKey && (
            <p className="text-xs text-destructive" data-testid="recovery-email-field-error">
              {t(fieldErrorKey)}
            </p>
          )}
          {/* Plain secondary text, not the blue note: it explains why the next screen confirms
              nothing, it does not ask the user to decide anything. */}
          <p className="text-xs text-muted-foreground" data-testid="recovery-email-note">
            {t('recovery.request.note')}
          </p>
        </div>

        {requestErrorKey && (
          <p className="text-xs text-destructive" data-testid="recovery-request-error">
            {translateIdentityError(t, requestErrorKey, errorRetryAfterSeconds)}
          </p>
        )}

        <Button className="w-full sm:w-fit" disabled={isSending || !isEmailAccepted} type="submit">
          {isSending && (
            <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="recovery-request-spinner" />
          )}
          {t('recovery.request.submit')}
        </Button>
      </form>
    </StepCard>
  );
};
